import React, { useState, useEffect } from 'react';
import { getRiskHeatmap, getDrillCompliance, getERPEffectiveness, getAuditLogs, scheduleReport, getScheduledReports } from '../../../service/api';

const MonitoringDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'overview' | 'risk-heatmap' | 'scheduled-reports' | 'audit-logs'>('overview');
    const [data, setData] = useState<any>({
        drillCompliance: null,
        erpEffectiveness: null,
        riskHeatmap: null,
        auditLogs: null,
        scheduledReports: null
    });
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [scheduleForm, setScheduleForm] = useState({
        reportType: 'Drill Compliance',
        frequency: 'Weekly',
        recipients: [],
        format: 'PDF',
        dayOfWeek: 'Monday',
        dayOfMonth: 1,
        time: '09:00'
    });

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const [drillRes, erpRes, riskRes, auditRes, reportsRes] = await Promise.all([
                getDrillCompliance(),
                getERPEffectiveness(),
                getRiskHeatmap(),
                getAuditLogs(),
                getScheduledReports()
            ]);

            setData({
                drillCompliance: drillRes.data.data,
                erpEffectiveness: erpRes.data.data,
                riskHeatmap: riskRes.data.data,
                auditLogs: auditRes.data.data,
                scheduledReports: reportsRes.data.data
            });
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleScheduleReport = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await scheduleReport(scheduleForm);
            alert('Report scheduled successfully!');
            setShowScheduleModal(false);
            fetchDashboardData();
        } catch (error) {
            console.error('Error scheduling report:', error);
            alert('Failed to schedule report');
        }
    };

    const getRiskColor = (riskLevel: number) => {
        if (riskLevel >= 80) return '#dc2626'; // Critical - Red
        if (riskLevel >= 60) return '#ea580c'; // High - Orange
        if (riskLevel >= 40) return '#f59e0b'; // Medium - Yellow
        if (riskLevel >= 20) return '#3b82f6'; // Low-Medium - Blue
        return '#10b981'; // Very Low - Green
    };

    if (loading) {
        return (
            <div style={{ padding: '24px' }}>
                <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '24px' }}>Emergency Preparedness Monitoring</h1>

                {/* Skeleton Loaders */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px', marginBottom: '24px' }}>
                    {[1, 2, 3].map(i => (
                        <div key={i} style={{
                            backgroundColor: 'white',
                            padding: '24px',
                            borderRadius: '12px',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                        }}>
                            <div style={{ height: '20px', backgroundColor: '#e5e7eb', borderRadius: '4px', marginBottom: '12px', width: '60%', animation: 'pulse 1.5s ease-in-out infinite' }} />
                            <div style={{ height: '40px', backgroundColor: '#e5e7eb', borderRadius: '4px', marginBottom: '8px', animation: 'pulse 1.5s ease-in-out infinite' }} />
                            <div style={{ height: '16px', backgroundColor: '#e5e7eb', borderRadius: '4px', width: '40%', animation: 'pulse 1.5s ease-in-out infinite' }} />
                        </div>
                    ))}
                </div>

                <div style={{
                    backgroundColor: 'white',
                    padding: '24px',
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    marginBottom: '24px'
                }}>
                    <div style={{ height: '24px', backgroundColor: '#e5e7eb', borderRadius: '4px', marginBottom: '16px', width: '30%', animation: 'pulse 1.5s ease-in-out infinite' }} />
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} style={{ height: '60px', backgroundColor: '#f9fafb', borderRadius: '8px', marginBottom: '12px', animation: 'pulse 1.5s ease-in-out infinite' }} />
                    ))}
                </div>

                <style>{`
                    @keyframes pulse {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.5; }
                    }
                `}</style>
            </div>
        );
    }

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
                    Emergency Preparedness Monitoring
                </h1>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '2px solid #e5e7eb' }}>
                {[
                    { id: 'overview', label: 'Overview' },
                    { id: 'risk-heatmap', label: 'Risk Heatmap' },
                    { id: 'scheduled-reports', label: 'Scheduled Reports' },
                    { id: 'audit-logs', label: 'Audit Logs' }
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id as any)}
                        style={{
                            padding: '12px 24px',
                            border: 'none',
                            backgroundColor: 'transparent',
                            borderBottom: activeTab === tab.id ? '2px solid #2563eb' : '2px solid transparent',
                            color: activeTab === tab.id ? '#2563eb' : '#6b7280',
                            fontWeight: activeTab === tab.id ? '600' : '400',
                            cursor: 'pointer',
                            fontSize: '14px',
                            marginBottom: '-2px'
                        }}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
                <>

            {/* Drill Compliance Section - ALWAYS VISIBLE */}
            <div style={{
                backgroundColor: 'white',
                padding: '24px',
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                marginBottom: '24px'
            }}>
                <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>
                    Drill Compliance Summary
                </h2>
                {data.drillCompliance?.summary ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
                        <div>
                            <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Total Drills</p>
                            <p style={{ fontSize: '24px', fontWeight: 'bold' }}>
                                {data.drillCompliance.summary.totalDrills || 0}
                            </p>
                        </div>
                        <div>
                            <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Completed</p>
                            <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#10b981' }}>
                                {data.drillCompliance.summary.completedDrills || 0}
                            </p>
                        </div>
                        <div>
                            <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Compliance Rate</p>
                            <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#3b82f6' }}>
                                {data.drillCompliance.summary.complianceRate || 0}%
                            </p>
                        </div>
                        <div>
                            <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Avg Participation</p>
                            <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#8b5cf6' }}>
                                {data.drillCompliance.summary.avgParticipationRate || 0}%
                            </p>
                        </div>
                    </div>
                ) : (
                    <div style={{
                        textAlign: 'center',
                        padding: '40px',
                        backgroundColor: '#f9fafb',
                        borderRadius: '8px',
                        border: '1px dashed #d1d5db'
                    }}>
                        <div style={{ fontSize: '48px', marginBottom: '12px' }}>📊</div>
                        <p style={{ color: '#9ca3af', fontSize: '14px', fontWeight: '500' }}>No drill compliance data available</p>
                        <p style={{ color: '#d1d5db', fontSize: '12px', marginTop: '8px' }}>Data will appear here once drills are scheduled and completed</p>
                    </div>
                )}
            </div>

            {/* ERP Effectiveness Section - ALWAYS VISIBLE */}
            <div style={{
                backgroundColor: 'white',
                padding: '24px',
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                marginBottom: '24px'
            }}>
                <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>
                    ERP Effectiveness Metrics
                </h2>
                {data.erpEffectiveness?.summary ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px' }}>
                        <div>
                            <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Total Executions</p>
                            <p style={{ fontSize: '24px', fontWeight: 'bold' }}>
                                {data.erpEffectiveness.summary.totalExecutions || 0}
                            </p>
                        </div>
                        <div>
                            <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Success Rate</p>
                            <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#10b981' }}>
                                {data.erpEffectiveness.summary.successRate || 0}%
                            </p>
                        </div>
                        <div>
                            <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>SLA Compliance</p>
                            <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#3b82f6' }}>
                                {data.erpEffectiveness.summary.slaComplianceRate || 0}%
                            </p>
                        </div>
                        <div>
                            <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Avg Time (min)</p>
                            <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#f59e0b' }}>
                                {data.erpEffectiveness.summary.avgExecutionTime || 0}
                            </p>
                        </div>
                        <div>
                            <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Total Deviations</p>
                            <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#ea580c' }}>
                                {data.erpEffectiveness.summary.totalDeviations || 0}
                            </p>
                        </div>
                    </div>
                ) : (
                    <div style={{
                        textAlign: 'center',
                        padding: '40px',
                        backgroundColor: '#f9fafb',
                        borderRadius: '8px',
                        border: '1px dashed #d1d5db'
                    }}>
                        <div style={{ fontSize: '48px', marginBottom: '12px' }}>📈</div>
                        <p style={{ color: '#9ca3af', fontSize: '14px', fontWeight: '500' }}>No ERP effectiveness data available</p>
                        <p style={{ color: '#d1d5db', fontSize: '12px', marginTop: '8px' }}>Metrics will appear here once ERP executions are completed</p>
                    </div>
                )}
            </div>

            {/* Recent Activity - ALWAYS VISIBLE */}
            <div style={{
                backgroundColor: 'white',
                padding: '24px',
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
                <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>
                    Recent ERP Executions
                </h2>
                {data.erpEffectiveness?.recentExecutions && data.erpEffectiveness.recentExecutions.length > 0 ? (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>
                                        Execution ID
                                    </th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>
                                        Plan Name
                                    </th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>
                                        Triggered At
                                    </th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>
                                        Status
                                    </th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>
                                        SLA
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.erpEffectiveness.recentExecutions.map((execution: any, idx: number) => (
                                    <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                        <td style={{ padding: '12px', fontSize: '14px' }}>{execution.executionId}</td>
                                        <td style={{ padding: '12px', fontSize: '14px' }}>{execution.planName}</td>
                                        <td style={{ padding: '12px', fontSize: '14px', color: '#6b7280' }}>
                                            {new Date(execution.triggeredAt).toLocaleString()}
                                        </td>
                                        <td style={{ padding: '12px', fontSize: '14px' }}>
                                            <span style={{
                                                padding: '4px 8px',
                                                borderRadius: '6px',
                                                backgroundColor: execution.status === 'Completed' ? '#10b98120' : '#f59e0b20',
                                                color: execution.status === 'Completed' ? '#10b981' : '#f59e0b',
                                                fontSize: '12px',
                                                fontWeight: '500'
                                            }}>
                                                {execution.status}
                                            </span>
                                        </td>
                                        <td style={{ padding: '12px', fontSize: '14px' }}>
                                            <span style={{
                                                padding: '4px 8px',
                                                borderRadius: '6px',
                                                backgroundColor: execution.slaMet ? '#10b98120' : '#dc262620',
                                                color: execution.slaMet ? '#10b981' : '#dc2626',
                                                fontSize: '12px',
                                                fontWeight: '500'
                                            }}>
                                                {execution.slaMet ? 'Met' : 'Missed'}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div style={{
                        textAlign: 'center',
                        padding: '60px 40px',
                        backgroundColor: '#f9fafb',
                        borderRadius: '8px',
                        border: '1px dashed #d1d5db'
                    }}>
                        <div style={{ fontSize: '64px', marginBottom: '16px' }}>🚨</div>
                        <p style={{ color: '#9ca3af', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                            No ERP executions yet
                        </p>
                        <p style={{ color: '#d1d5db', fontSize: '14px' }}>
                            Recent execution history will appear here once you trigger emergency response plans
                        </p>
                    </div>
                )}
            </div>
                </>
            )}

            {/* Risk Heatmap Tab */}
            {activeTab === 'risk-heatmap' && (
                <div style={{
                    backgroundColor: 'white',
                    padding: '24px',
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                    <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>
                        Risk Heatmap by Location
                    </h2>
                    {data.riskHeatmap?.locations && data.riskHeatmap.locations.length > 0 ? (
                        <>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                                {data.riskHeatmap.locations.map((location: any, idx: number) => (
                                    <div
                                        key={idx}
                                        style={{
                                            padding: '16px',
                                            borderRadius: '8px',
                                            backgroundColor: getRiskColor(location.riskScore) + '20',
                                            border: `2px solid ${getRiskColor(location.riskScore)}`,
                                            textAlign: 'center'
                                        }}
                                    >
                                        <p style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                                            {location.name}
                                        </p>
                                        <p style={{
                                            fontSize: '24px',
                                            fontWeight: 'bold',
                                            color: getRiskColor(location.riskScore),
                                            marginBottom: '4px'
                                        }}>
                                            {location.riskScore}%
                                        </p>
                                        <p style={{ fontSize: '12px', color: '#6b7280' }}>
                                            {location.riskLevel}
                                        </p>
                                        {location.totalIncidents > 0 && (
                                            <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '8px' }}>
                                                {location.totalIncidents} incident(s)
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                            {/* Legend */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                                <span style={{ fontSize: '12px', fontWeight: '600' }}>Risk Levels:</span>
                                {[
                                    { level: 'Very Low', color: '#10b981', range: '0-20%' },
                                    { level: 'Low-Medium', color: '#3b82f6', range: '20-40%' },
                                    { level: 'Medium', color: '#f59e0b', range: '40-60%' },
                                    { level: 'High', color: '#ea580c', range: '60-80%' },
                                    { level: 'Critical', color: '#dc2626', range: '80-100%' }
                                ].map((item, idx) => (
                                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <div style={{
                                            width: '20px',
                                            height: '20px',
                                            borderRadius: '4px',
                                            backgroundColor: item.color
                                        }} />
                                        <span style={{ fontSize: '12px' }}>{item.level} ({item.range})</span>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div style={{
                            textAlign: 'center',
                            padding: '60px 40px',
                            backgroundColor: '#f9fafb',
                            borderRadius: '8px',
                            border: '1px dashed #d1d5db'
                        }}>
                            <div style={{ fontSize: '64px', marginBottom: '16px' }}>🗺️</div>
                            <p style={{ color: '#9ca3af', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                                No risk data available
                            </p>
                            <p style={{ color: '#d1d5db', fontSize: '14px' }}>
                                Risk heatmap data will appear here once risk assessments are completed
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* Scheduled Reports Tab */}
            {activeTab === 'scheduled-reports' && (
                <div style={{
                    backgroundColor: 'white',
                    padding: '24px',
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                        <h2 style={{ fontSize: '18px', fontWeight: 'bold', margin: 0 }}>
                            Scheduled Reports
                        </h2>
                        <button
                            onClick={() => setShowScheduleModal(true)}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#2563eb',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontWeight: '500',
                                fontSize: '14px'
                            }}
                        >
                            + Schedule New Report
                        </button>
                    </div>

                    {data.scheduledReports && data.scheduledReports.length > 0 ? (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Report Type</th>
                                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Frequency</th>
                                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Next Run</th>
                                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Recipients</th>
                                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Format</th>
                                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.scheduledReports.map((report: any, idx: number) => (
                                        <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                            <td style={{ padding: '12px', fontSize: '14px' }}>{report.reportType}</td>
                                            <td style={{ padding: '12px', fontSize: '14px' }}>{report.frequency}</td>
                                            <td style={{ padding: '12px', fontSize: '14px', color: '#6b7280' }}>
                                                {report.nextRun ? new Date(report.nextRun).toLocaleString() : '-'}
                                            </td>
                                            <td style={{ padding: '12px', fontSize: '14px' }}>
                                                {report.recipients?.length || 0} recipient(s)
                                            </td>
                                            <td style={{ padding: '12px', fontSize: '14px' }}>{report.format}</td>
                                            <td style={{ padding: '12px', fontSize: '14px' }}>
                                                <span style={{
                                                    padding: '4px 8px',
                                                    borderRadius: '6px',
                                                    backgroundColor: report.active ? '#10b98120' : '#6b728020',
                                                    color: report.active ? '#10b981' : '#6b7280',
                                                    fontSize: '12px',
                                                    fontWeight: '500'
                                                }}>
                                                    {report.active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div style={{
                            textAlign: 'center',
                            padding: '60px 40px',
                            backgroundColor: '#f9fafb',
                            borderRadius: '8px',
                            border: '1px dashed #d1d5db'
                        }}>
                            <div style={{ fontSize: '64px', marginBottom: '16px' }}>📧</div>
                            <p style={{ color: '#9ca3af', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                                No scheduled reports
                            </p>
                            <p style={{ color: '#d1d5db', fontSize: '14px', marginBottom: '16px' }}>
                                Schedule automated reports to be sent via email
                            </p>
                            <button
                                onClick={() => setShowScheduleModal(true)}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#2563eb',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontWeight: '500',
                                    fontSize: '14px'
                                }}
                            >
                                Schedule Your First Report
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Audit Logs Tab */}
            {activeTab === 'audit-logs' && (
                <div style={{
                    backgroundColor: 'white',
                    padding: '24px',
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                    <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '24px' }}>
                        Audit Logs & Compliance History
                    </h2>

                    {data.auditLogs && data.auditLogs.length > 0 ? (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Timestamp</th>
                                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Action</th>
                                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>User</th>
                                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Entity Type</th>
                                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Entity ID</th>
                                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>IP Address</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.auditLogs.map((log: any, idx: number) => (
                                        <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                            <td style={{ padding: '12px', fontSize: '14px', color: '#6b7280' }}>
                                                {log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}
                                            </td>
                                            <td style={{ padding: '12px', fontSize: '14px' }}>
                                                <span style={{
                                                    padding: '4px 8px',
                                                    borderRadius: '6px',
                                                    backgroundColor: log.action === 'CREATE' ? '#10b98120' :
                                                        log.action === 'UPDATE' ? '#3b82f620' :
                                                        log.action === 'DELETE' ? '#dc262620' : '#6b728020',
                                                    color: log.action === 'CREATE' ? '#10b981' :
                                                        log.action === 'UPDATE' ? '#3b82f6' :
                                                        log.action === 'DELETE' ? '#dc2626' : '#6b7280',
                                                    fontSize: '12px',
                                                    fontWeight: '500'
                                                }}>
                                                    {log.action}
                                                </span>
                                            </td>
                                            <td style={{ padding: '12px', fontSize: '14px' }}>
                                                {log.userName || log.userId || 'System'}
                                            </td>
                                            <td style={{ padding: '12px', fontSize: '14px' }}>{log.entityType || '-'}</td>
                                            <td style={{ padding: '12px', fontSize: '14px', fontFamily: 'monospace', color: '#6b7280' }}>
                                                {log.entityId ? String(log.entityId).substring(0, 8) + '...' : '-'}
                                            </td>
                                            <td style={{ padding: '12px', fontSize: '14px', fontFamily: 'monospace', color: '#6b7280' }}>
                                                {log.ipAddress || '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div style={{
                            textAlign: 'center',
                            padding: '60px 40px',
                            backgroundColor: '#f9fafb',
                            borderRadius: '8px',
                            border: '1px dashed #d1d5db'
                        }}>
                            <div style={{ fontSize: '64px', marginBottom: '16px' }}>📋</div>
                            <p style={{ color: '#9ca3af', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                                No audit logs available
                            </p>
                            <p style={{ color: '#d1d5db', fontSize: '14px' }}>
                                Audit logs will appear here as system activities are recorded
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* Schedule Report Modal */}
            {showScheduleModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '24px',
                        borderRadius: '12px',
                        maxWidth: '600px',
                        width: '90%',
                        maxHeight: '90vh',
                        overflowY: 'auto'
                    }}>
                        <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '20px' }}>
                            Schedule New Report
                        </h3>
                        <form onSubmit={handleScheduleReport}>
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                                    Report Type
                                </label>
                                <select
                                    value={scheduleForm.reportType}
                                    onChange={(e) => setScheduleForm({ ...scheduleForm, reportType: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '6px',
                                        border: '1px solid #d1d5db',
                                        fontSize: '14px'
                                    }}
                                    required
                                >
                                    <option value="Drill Compliance">Drill Compliance</option>
                                    <option value="ERP Effectiveness">ERP Effectiveness</option>
                                    <option value="Risk Assessment">Risk Assessment</option>
                                    <option value="Audit Summary">Audit Summary</option>
                                </select>
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                                    Frequency
                                </label>
                                <select
                                    value={scheduleForm.frequency}
                                    onChange={(e) => setScheduleForm({ ...scheduleForm, frequency: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '6px',
                                        border: '1px solid #d1d5db',
                                        fontSize: '14px'
                                    }}
                                    required
                                >
                                    <option value="Daily">Daily</option>
                                    <option value="Weekly">Weekly</option>
                                    <option value="Monthly">Monthly</option>
                                    <option value="Quarterly">Quarterly</option>
                                </select>
                            </div>

                            {scheduleForm.frequency === 'Weekly' && (
                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                                        Day of Week
                                    </label>
                                    <select
                                        value={scheduleForm.dayOfWeek}
                                        onChange={(e) => setScheduleForm({ ...scheduleForm, dayOfWeek: e.target.value })}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            borderRadius: '6px',
                                            border: '1px solid #d1d5db',
                                            fontSize: '14px'
                                        }}
                                    >
                                        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                                            <option key={day} value={day}>{day}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {scheduleForm.frequency === 'Monthly' && (
                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                                        Day of Month
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="28"
                                        value={scheduleForm.dayOfMonth}
                                        onChange={(e) => setScheduleForm({ ...scheduleForm, dayOfMonth: parseInt(e.target.value) })}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            borderRadius: '6px',
                                            border: '1px solid #d1d5db',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>
                            )}

                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                                    Time
                                </label>
                                <input
                                    type="time"
                                    value={scheduleForm.time}
                                    onChange={(e) => setScheduleForm({ ...scheduleForm, time: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '6px',
                                        border: '1px solid #d1d5db',
                                        fontSize: '14px'
                                    }}
                                    required
                                />
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                                    Format
                                </label>
                                <select
                                    value={scheduleForm.format}
                                    onChange={(e) => setScheduleForm({ ...scheduleForm, format: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '6px',
                                        border: '1px solid #d1d5db',
                                        fontSize: '14px'
                                    }}
                                    required
                                >
                                    <option value="PDF">PDF</option>
                                    <option value="Excel">Excel</option>
                                    <option value="CSV">CSV</option>
                                </select>
                            </div>

                            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                                <button
                                    type="button"
                                    onClick={() => setShowScheduleModal(false)}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#e5e7eb',
                                        color: '#374151',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontWeight: '500',
                                        fontSize: '14px'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#2563eb',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontWeight: '500',
                                        fontSize: '14px'
                                    }}
                                >
                                    Schedule Report
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MonitoringDashboard;
